<template>
  <div>
    <!-- Error notification -->
    <div v-if="messageError" class="notification is-danger actionNotification">
      <button class="delete" @click.prevent="removeError"></button>
      {{ messageError }}
    </div>
    
    <div data-component="Lot/NewVisit" class="is-actions-and-works-container">
      <!-- Empty state or loading -->
      <template v-if="controls.length === 0">
        <div v-if="ready" data-component="Folder/Informations-empty">
          <p>Aucune action n'a encore été effectuée pour ce lot</p>
        </div>
        <div v-else class="is-flex has-item-centered has-item-aligned-center">
          <LoadingSpinner />
        </div>
      </template>

      <!-- Controls list -->
      <template v-else>
        <div class="is-flex is-unfold-all-container">
          <a href="#none" class="unfold-all" @click.prevent="handleUnfoldAll">
            {{ isAllUnfolded ? 'Tout replier' : 'Tout déplier' }}
          </a>
        </div>

        <div class="listing">
          <div class="body">
            <div 
              v-for="(control, i) in controls" 
              :key="`line-${i}`" 
              class="line"
            >
              <!-- Control header -->
              <div class="columns">
                <div class="column is-flex has-item-started">
                  <!-- Toggle button -->
                  <div class="is-toggler-button">
                    <button class="unfold-line" @click="handleUnfold(control.id)">
                      <i :class="control.unfolded ? 'ico short-arrow-up' : 'ico short-arrow-down'" />
                    </button>
                  </div>

                  <!-- Title and category -->
                  <div class="contains-title-and-category">
                    <p class="is-category">{{ control.position }} - {{ control.section }}</p>
                    <p class="is-title">{{ control.label }}</p>
                  </div>

                  <!-- Status radios -->
                  <div class="is-flex contains-statuses">
                    <Radio
                      label="À faire"
                      :name="`control-status-${control.id}`"
                      :id="`control-${control.id}-status-todo`"
                      :value="STATUS_TODO"
                      :checked="STATUS_TODO === control.status"
                      @change="handleChangeStatus(control.id, STATUS_TODO)"
                    />
                    <Radio
                      label="En cours"
                      :name="`control-status-${control.id}`"
                      :id="`control-${control.id}-status-doing`"
                      :value="STATUS_DOING"
                      :checked="STATUS_DOING === control.status"
                      @change="handleChangeStatus(control.id, STATUS_DOING)"
                    />
                    <Radio
                      label="Effectué"
                      :name="`control-status-${control.id}`"
                      :id="`control-${control.id}-status-done`"
                      :value="STATUS_DONE"
                      :checked="STATUS_DONE === control.status"
                      @change="handleChangeStatus(control.id, STATUS_DONE)"
                    />
                  </div>

                  <!-- Icons -->
                  <div class="is-flex contains-icons">
                    <template v-if="hasComments(control)">
                      <i 
                        :class="['ico', 'comment', hasComments(control) ? 'active' : '']" 
                        @click="handleUnfold(control.id)"
                        :title="`${countComments(control)} commentaire(s)`"
                      />
                      <i 
                        :class="['ico', 'photo', hasPictures(control) ? 'active' : '']" 
                        @click="handleUnfold(control.id)"
                        :title="`${countPictures(control)} photo(s)`"
                      />
                    </template>
                  </div>
                </div>
              </div>

              <!-- Comments section -->
              <div :class="['is-comment-container', control.unfolded ? 'show' : 'hide']">
                <!-- No comments message -->
                <p v-if="control.comments.length <= 0" class="no-comment">
                  Aucun commentaire ou photo
                </p>

                <!-- Existing comments -->
                <div 
                  v-for="(comment, commentIndex) in control.comments" 
                  :key="`comment-${commentIndex}`"
                  class="comment-wrapper"
                >
                  <p class="author">
                    {{ comment.by }},&nbsp;
                    <span class="at">{{ formatDate(comment.at) }}</span>
                  </p>
                  <p class="content">{{ comment.content }}</p>
                  
                  <!-- Pictures - OPTIMISATION: lazy loading natif -->
                  <div v-if="comment.pictures.length > 0" class="picture-area">
                    <img
                      v-for="(picture, pictureIndex) in comment.pictures"
                      :key="`picture-${pictureIndex}-${comment.at}`"
                      :src="picture.src"
                      :alt="`n°${pictureIndex}`"
                      loading="lazy"
                      @click="handleDisplayImages(pictureIndex, comment.pictures)"
                    />
                  </div>
                </div>

                <!-- New comment section -->
                <h5>Nouveau commentaire</h5>
                <div class="comment-wrapper new-comment">
                  <!-- Comment textarea -->
                  <div class="is-comment-pane open">
                    <textarea 
                      placeholder="Votre commentaire ..." 
                      :value="control.newComment.text"
                      @input="handleChangeControlComment(control.id, $event)"
                      rows="3"
                    ></textarea>
                  </div>

                  <!-- Pictures upload -->
                  <div class="is-pictures-pane open">
                    <div class="is-flex has-images has-item-started">
                      <div 
                        v-for="picture in control.newComment.pictures"
                        :key="`image-n-${control.id}-${picture.id}`"
                        class="is-wrapping-image"
                      >
                        <img 
                          :src="picture.file.src" 
                          :alt="`Complément n° ${control.id}-${picture.id}`" 
                        />
                        <i 
                          class="ico trash" 
                          @click.prevent="handleRemoveControlPicture(control.id, picture.id)"
                        />
                      </div>
                      <UploadImage 
                        :id="`image-for-${control.id}`" 
                        @click.stop="handleDisplayCamera(control.id)"
                      />
                    </div>
                  </div>
                </div>

                <!-- Add comment button -->
                <div class="is-flex has-item-ended is-button-group">
                  <Button 
                    :class="['is-small', { 'is-loading': savingComments[control.id] }]"
                    :disabled="savingComments[control.id]"
                    @click="handleSendControlComment(control.id, control.newComment)"
                  >
                    Ajouter
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
/* eslint-disable vue/no-reserved-component-names */
import moment from 'moment'
import LoadingSpinner from '@/components/Common/Spinner.vue'
import Radio from '@/components/Widget/Radio.vue'
import Button from '@/components/Widget/Button.vue'
import UploadImage from '@/components/Widget/UploadImage.vue'
import PROGRESS_STATUS from '@/utils/progressStatus'

// Status constants from progressStatus
const STATUS_TODO = PROGRESS_STATUS.TODO
const STATUS_DOING = PROGRESS_STATUS.DOING
const STATUS_DONE = PROGRESS_STATUS.DONE

export default {
  name: 'ActionsAndWorks',
  
  components: {
    LoadingSpinner,
    Radio,
    Button,
    UploadImage,
  },

  data() {
    return {
      STATUS_TODO,
      STATUS_DOING,
      STATUS_DONE,
      spinnerButton: '/images/loading.gif',
      ready: false,
      controls: [],
      messageError: null,
      savingComments: {}, // Track saving state per control { controlId: boolean }
      uploadingImages: {}, // Track image upload state per control { controlId: boolean }
      updatingStatus: {}, // Track status update state per control { controlId: boolean }
    }
  },

  computed: {
    propertyId() {
      return this.$route.params.id
    },

    isAllUnfolded() {
      return this.controls.every(control => control.unfolded === true)
    },
  },

  methods: {
    /**
     * Gestion de l'ajout d'images via la caméra ou sélection de fichier
     */
    handleDisplayCamera(controlId) {
      // Ouvrir le CameraViewer avec le contexte d'action
      this.$store.dispatch('cameraViewer/open', { 
        id: controlId, 
        isRestoration: false, 
        isAction: true 
      })
    },

    /**
     * Gère la sélection de fichiers
     */
    async handleFileSelection(controlId, files) {
      const control = this.controls.find(c => c.id === controlId)
      if (!control) {
        console.error('Control non trouvé:', controlId)
        return
      }

      // Traiter chaque fichier
      for (let i = 0; i < files.length; i++) {
        const file = files[i]
        
        try {
          // Créer une URL de prévisualisation
          const src = await this.readFileAsDataURL(file)
          
          // Ajouter l'image au contrôle
          const pictureId = Date.now() + i
          control.newComment.pictures.push({
            id: pictureId,
            file: {
              src: src,
              alt: file.name,
              raw: file
            }
          })
        } catch (error) {
          console.error('Erreur lors de la lecture du fichier:', error)
          this.messageError = 'Erreur lors du chargement de l\'image'
        }
      }
    },

    /**
     * Lit un fichier et retourne une Data URL
     */
    readFileAsDataURL(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader()
        
        reader.onload = (e) => {
          resolve(e.target.result)
        }
        
        reader.onerror = (error) => {
          reject(error)
        }
        
        reader.readAsDataURL(file)
      })
    },

    /**
     * Fonction controlToState - Mapping identique à l'app React
     * Transforme un control depuis IndexedDB vers le format d'affichage
     * 
     * Structure des données dans IndexedDB:
     * - control.interventionPoint contient les données embarquées (section, label, position)
     * - control.comments contient tous les commentaires avec author, createdAt, text, picturesUrl
     */
    controlToState(control) {
      return {
        id: control.id,
        // InterventionPoint data (embarqué dans control.interventionPoint)
        section: control.interventionPoint?.section?.name || 'Sans section',
        label: control.interventionPoint?.label || 'Point de contrôle',
        position: control.interventionPoint?.position || '',
        // Status
        status: control.actionStatus || PROGRESS_STATUS.TODO,
        unfolded: false,
        // Comments (mappés depuis control.comments)
        comments: this.mapComments(control.comments || []),
        newComment: {
          text: '',
          pictures: []
        }
      }
    },

    /**
     * Mapping des commentaires - identique à l'app React
     */
    mapComments(comments) {
      if (!Array.isArray(comments)) {
        return []
      }
      
      return comments.map(comment => ({
        by: comment.author?.fullName || 
            `${comment.author?.firstName || ''} ${comment.author?.lastName || ''}`.trim() || 
            'Utilisateur',
        at: comment.createdAt,
        content: comment.text || '',
        pictures: this.mapPictures(comment.picturesUrl || [])
      }))
    },

    /**
     * Mapping des images - identique à l'app React
     */
    mapPictures(picturesUrl) {
      if (!Array.isArray(picturesUrl)) {
        return []
      }
      
      // Importer dynamiquement getEnv pour obtenir l'URL de l'API
      const getApiUrl = () => {
        if (window.env && window.env.VUE_APP_API_URL && window.env.VUE_APP_API_URL !== '__VUE_APP_API_URL__') {
          return window.env.VUE_APP_API_URL
        }
        if (process.env.VUE_APP_API_URL) {
          return process.env.VUE_APP_API_URL
        }
        return '/api'
      }
      
      const API_URL = getApiUrl()
      
      return picturesUrl.map(picture => {
        let src = picture
        
        // Si c'est un objet, extraire l'URL
        if (typeof picture === 'object' && picture !== null) {
          src = picture.thumbnail || picture.raw || picture.src || picture
        }
        
        // Si c'est une string qui n'est pas déjà une URL complète (http/https/data:)
        if (typeof src === 'string' && !src.startsWith('http://') && !src.startsWith('https://') && !src.startsWith('data:')) {
          // C'est un nom de fichier, construire l'URL sans le préfixe /api/
          const baseUrl = API_URL.replace(/\/api$/, '')
          src = `${baseUrl}/uploads/comment/${src}`
        }
        
        return {
          src: src,
          originalSrc: src,
          alt: typeof picture === 'object' ? picture.alt || '' : ''
        }
      })
    },

    async fetchControls() {
      this.ready = false
      this.messageError = null
      
      try {
        // Importer le service Dexie
        const dexieService = (await import('../../services/dexieService')).default
        
        // Récupérer la propriété depuis IndexedDB
        const property = await dexieService.getProperty(this.propertyId)
        
        if (!property) {
          console.warn('Propriété non trouvée')
          this.controls = []
          return
        }
        
        // OPTIMISATION 1: Identifier d'abord les IDs des controls nécessaires
        const neededControlIds = new Set()
        
        if (property.visits && property.visits.length > 0) {
          property.visits.forEach(visit => {
            if (visit.controls && visit.controls.length > 0) {
              visit.controls.forEach(controlRef => {
                // Ne garder que les contrôles avec des actions (todo ou doing)
                if (controlRef.actionStatus === PROGRESS_STATUS.TODO || controlRef.actionStatus === PROGRESS_STATUS.DOING) {
                  neededControlIds.add(controlRef.id)
                }
              })
            }
          })
        }
        
        if (neededControlIds.size === 0) {
          this.controls = []
          return
        }
        
        // OPTIMISATION 2: Charger SEULEMENT les controls nécessaires avec une requête ciblée
        const db = dexieService.db
        const controlsTable = db.table('controls')
        const controlsFromDB = await controlsTable
          .where('id')
          .anyOf([...neededControlIds])
          .toArray()
        
        // Créer une Map pour un accès rapide par ID
        const controlsMap = new Map(
          controlsFromDB.map(ctrl => [ctrl.id, ctrl])
        )
        
        // OPTIMISATION 3: Éviter les doublons avec un Set
        const controlsToDisplay = []
        const seen = new Set()
        
        if (property.visits && property.visits.length > 0) {
          for (const visit of property.visits) {
            if (visit.controls && visit.controls.length > 0) {
              for (const controlRef of visit.controls) {
                if ((controlRef.actionStatus === PROGRESS_STATUS.TODO || controlRef.actionStatus === PROGRESS_STATUS.DOING) && !seen.has(controlRef.id)) {
                  seen.add(controlRef.id)
                  
                  const fullControl = controlsMap.get(controlRef.id)
                  
                  if (fullControl) {
                    const mappedControl = this.controlToState(fullControl)
                    controlsToDisplay.push(mappedControl)
                  } else {
                    console.warn(`Control ${controlRef.id} non trouvé dans IndexedDB`)
                    const mappedControl = this.controlToState(controlRef)
                    controlsToDisplay.push(mappedControl)
                  }
                }
              }
            }
          }
        }
        
        // Trier les contrôles par position
        controlsToDisplay.sort((a, b) => {
          const posA = String(a.position || '')
          const posB = String(b.position || '')
          return posA.localeCompare(posB, undefined, { numeric: true, sensitivity: 'base' })
        })
        
        this.controls = controlsToDisplay
        
      } catch (error) {
        console.error('Erreur lors du chargement des actions et travaux:', error)
        console.error('Stack trace:', error.stack)
        this.messageError = 'Erreur lors du chargement des données'
      } finally {
        this.ready = true
      }
    },

    handleUnfoldAll() {
      const shouldUnfold = !this.isAllUnfolded
      this.controls.forEach(control => {
        control.unfolded = shouldUnfold
      })
    },

    handleUnfold(id) {
      const control = this.controls.find(c => c.id === id)
      if (control) {
        control.unfolded = !control.unfolded
      }
    },

    async handleChangeStatus(id, status) {
      const control = this.controls.find(c => c.id === id)
      if (!control) {
        console.error('Control non trouvé:', id)
        return
      }

      // Prevent double calls - check if already updating
      if (this.updatingStatus[id]) {
        return
      }

      // Check if status is actually changing
      if (control.status === status) {
        return
      }

      // Mark as updating
      this.updatingStatus[id] = true

      // Optimistically update the UI
      const previousStatus = control.status
      control.status = status

      try {
        // Import the helper function
        const { updateControl } = await import('@/services/mutationHelpers')
        
        // Save the status change (will try API, or store in mutation table if offline)
        const result = await updateControl(id, { actionStatus: status })
        
        if (result.success) {
          // Status updated successfully
        } else {
          // Rollback on error
          control.status = previousStatus
          this.messageError = result.error || 'Erreur lors de la mise à jour du statut'
        }
      } catch (error) {
        // Rollback on error
        console.error('Erreur lors de la mise à jour du statut:', error)
        control.status = previousStatus
        this.messageError = 'Erreur lors de la mise à jour du statut'
      } finally {
        // Reset updating flag
        this.updatingStatus[id] = false
      }
    },

    handleChangeControlComment(id, event) {
      const control = this.controls.find(c => c.id === id)
      if (control) {
        control.newComment.text = event.target.value
      }
    },

    handleRemoveControlPicture(controlId, pictureId) {
      const control = this.controls.find(c => c.id === controlId)
      if (control) {
        control.newComment.pictures = control.newComment.pictures.filter(
          p => p.id !== pictureId
        )
      }
    },

    async handleSendControlComment(id, newComment) {
      if (!newComment.text.trim() && newComment.pictures.length === 0) {
        return
      }

      // Vérifier si une sauvegarde est déjà en cours pour ce control
      if (this.savingComments[id]) {
        return
      }

      const control = this.controls.find(c => c.id === id)
      if (!control) {
        console.error('Control non trouvé:', id)
        return
      }

      // Marquer le bouton comme en cours de sauvegarde (Vue 3)
      this.savingComments[id] = true

      try {
        // Récupérer l'utilisateur connecté
        const dexieService = (await import('../../services/dexieService')).default
        const currentUser = await dexieService.getUser()
        
        // Importer le helper de mutation pour les commentaires
        const { createComment } = await import('@/services/mutationHelpers')
        
        // Préparer les images avec leurs données en base64
        const pictures = newComment.pictures.map(p => ({
          id: p.id,
          src: p.file.src, // C'est déjà une data URL (base64) grâce à readFileAsDataURL
          thumbnail: p.file.src, // Utiliser la même pour la miniature
          raw: p.file.src, // Utiliser la même pour l'affichage complet
          name: p.file.alt || p.file.name || 'Photo'
        }))
        
        // Préparer les données du commentaire
        const commentData = {
          text: newComment.text.trim(),
          controlId: id,
          control: { id },
          propertyId: this.propertyId,
          pictures: pictures,
          createdAt: new Date().toISOString(),
          author: {
            id: currentUser?.id || '',
            fullName: currentUser?.fullName || 'Utilisateur',
            firstName: currentUser?.firstName || 'Utilisateur',
            lastName: currentUser?.lastName || ''
          }
        }
        
        // Créer le commentaire via le mutation service
        // Cela tentera l'API et stockera dans mutations si échec
        const result = await createComment(commentData)
        
        if (result.success) {
          // Réinitialiser le formulaire d'abord
          control.newComment.text = ''
          control.newComment.pictures = []
          
          // Recharger le control depuis IndexedDB pour afficher le nouveau commentaire
          // Cela évite les doublons car localUpdate a déjà ajouté le commentaire dans IndexedDB
          try {
            const dexieService = (await import('../../services/dexieService')).default
            const db = dexieService.db
            const controlsTable = db.table('controls')
            const updatedControl = await controlsTable.get(id)
            
            if (updatedControl) {
              // Mettre à jour les commentaires de ce control dans l'affichage
              control.comments = this.mapComments(updatedControl.comments || [])
            }
          } catch (error) {
            console.error('Erreur lors du rechargement du control:', error)
          }
          
        } else {
          // Erreur lors de la sauvegarde
          this.messageError = result.error || 'Erreur lors de l\'enregistrement du commentaire'
        }
        
      } catch (error) {
        console.error('Erreur lors de l\'envoi du commentaire:', error)
        this.messageError = 'Erreur lors de l\'enregistrement du commentaire'
      } finally {
        // Désactiver l'état de sauvegarde (Vue 3)
        this.savingComments[id] = false
      }
    },

    removeError() {
      this.messageError = null
    },

    formatDate(dateString) {
      return moment(dateString).format('DD/MM/YYYY')
    },

    hasComments(control) {
      return control.comments && control.comments.some(comment => comment.content && comment.content.trim())
    },

    hasPictures(control) {
      return control.comments && control.comments.some(comment => 
        comment.pictures && comment.pictures.length > 0
      )
    },

    countComments(control) {
      if (!control.comments) return 0
      return control.comments.filter(comment => comment.content && comment.content.trim()).length
    },

    countPictures(control) {
      if (!control.comments) return 0
      return control.comments.reduce((total, comment) => 
        total + (comment.pictures ? comment.pictures.length : 0), 0
      )
    },

    /**
     * Gère la réception d'une photo capturée depuis le CameraViewer
     */
    handleCameraPhotoCaptured(event) {
      const { controlId, picture } = event.detail
      
      if (!controlId || !picture) {
        console.error('Données de photo invalides:', event.detail)
        return
      }
      
      // Trouver le control correspondant
      const control = this.controls.find(c => c.id === controlId)
      if (!control) {
        console.error('Control non trouvé:', controlId)
        return
      }
      
      // Ajouter la photo au control
      control.newComment.pictures.push(picture)
    },
  },

  mounted() {
    this.fetchControls()
    
    // Écouter les événements de photo capturée depuis CameraViewer
    window.addEventListener('camera-photo-captured', this.handleCameraPhotoCaptured)
  },
  
  beforeUnmount() {
    // Nettoyer l'event listener
    window.removeEventListener('camera-photo-captured', this.handleCameraPhotoCaptured)
  }
}
</script>

<style lang="scss" scoped>
@import './ActionsAndWorks.scss';
</style>
